import React from 'react';
import styled from 'styled-components/macro';
import { space, layout, SpaceProps } from 'styled-system';

import { Heading2, Text } from '../SubComponents/Text';
import { CloseButton } from '../SubComponents/Buttons';
import { ReactComponent as Close } from '../Assets/Icons/x.svg';
import SummaryInfo from './SummaryInfo';
import BookingTimer from './BookingTimer';

const defaultBoxShadow = '0 4px 6px rgba(50, 50, 93, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)';

const SummaryBox = styled.div<{ timer: boolean }>`
  display: flex;
  position: relative;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.summaryBackgroundColor};
  border-radius: 8px;
  box-shadow: ${(props) => (props.theme.shadows ? defaultBoxShadow : 'none')};
  padding: 25px 20px 20px;
  text-align: center;
  position: relative;

  border-bottom-left-radius: ${(props) => (props.timer ? 0 : '8px')};
  border-bottom-right-radius: ${(props) => (props.timer ? 0 : '8px')};

  ${space}
  ${layout}
`;

const SummaryWrapper = styled.div<SpaceProps>`
  ${space}
`;

interface SummaryProps {
  children: React.ReactNode;
  closable: boolean;
  heading: string;
  shopName: string;
  orderEditable?: boolean;
  prices: {
    voucherValue?: number;
    voucherValueExtras?: number;
    sailingPrice?: number;
    extrasPrice?: number;
    warrantyPrice?: number;
    paymentValue?: number;
    fullPrice?: number;
    balance?: number;
    toBePaid?: number;
    toBeRefunded?: number;
  };
  order?: { itemId: string; quantity: number }[];
  items?: { id: string; name: string; price: number; donation?: boolean | null }[];
  vouchers: { id: string; category: string }[];
  payments?: { id: string }[];
  isWarrantyApplied?: boolean;
  warrantyEditable?: boolean;
  onClose: () => void;
  modifiedDate?: string | null;
  expiresDate?: string | null;
  showBookingTimer?: boolean;
}

const Summary = ({
  children,
  closable,
  heading,
  shopName,
  orderEditable,
  prices,
  order,
  items,
  vouchers,
  payments,
  isWarrantyApplied,
  warrantyEditable,
  onClose,
  modifiedDate,
  expiresDate,
  showBookingTimer = false,
}: SummaryProps) => {
  const isTimerVisible = Boolean(modifiedDate && expiresDate && showBookingTimer);

  return (
    <SummaryWrapper mb="4">
      <SummaryBox timer={isTimerVisible}>
        <>
          {closable && (
            <CloseButton icon={Close} altText="Close payment modal" onClick={onClose} />
          )}

          <Heading2 color="#fff" mb="2">
            {heading}
          </Heading2>

          <Text color="#fff" mb="3">
            {shopName}
          </Text>

          {children}

          <SummaryInfo
            orderEditable={orderEditable}
            prices={prices}
            order={order}
            items={items}
            vouchers={vouchers}
            payments={payments}
            isWarrantyApplied={isWarrantyApplied}
            warrantyEditable={warrantyEditable}
          />
        </>
      </SummaryBox>

      {showBookingTimer && (
        <BookingTimer modifiedDate={modifiedDate} expiresDate={expiresDate} flatTop />
      )}
    </SummaryWrapper>
  );
};

export default Summary;
